import React, { FunctionComponent } from 'react'
import { Button } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import {
  useSaleManager,
  useTrainingModeManager,
  useVdFeatureManager,
} from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import { selectCurrentSaleAsSale } from '../../../state/slices/currentSale/currentSaleSlice'
import { isQuote, isReturn, saleIsExchange } from '../../../functions/sale'
import { FEATURE_VALIDATE_RETURN_QUANTITIES } from '../../../constants/wr-features'
import { SaleSyncStatus } from '../../../models/sale.constants'
import { selectIsFinalisingSale } from '../../../state/slices/uiStateSlice'
import { useIsOffline } from '../../shared/useIsOffline'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { FEATURE_ENABLE_INVOICING_PAYMENT_REQUEST_QUOTE_DELIVERY } from '../../../constants'
import { useCloseSaleConfirmation } from './useCloseSaleConfirmation'
import { useCompleteTutorial } from './useCompleteTutorial'
import { QuotePaymentRequestState } from './QuotePaymentRequestCard'
import { isAbpOnlyConfirmationFlow } from './abp-confirmation-flow'

interface SaleActionBarProps {
  actionDisabled?: boolean
  isPickedUp: boolean
  setIsPickedUp: (value: boolean) => void
  quotePaymentRequest?: QuotePaymentRequestState
  onShareLinkReady?: (proposalUrl: string) => Promise<void>
}

const COMPLETE_SALE_CONTEXT = 'Complete sale button'

export const SaleActionBar: FunctionComponent<SaleActionBarProps> = ({
  actionDisabled,
  isPickedUp,
  setIsPickedUp,
  quotePaymentRequest,
  onShareLinkReady,
}) => {
  const trainingModeManager = useTrainingModeManager()
  const sale = useAppSelector(selectCurrentSaleAsSale)
  const isFinalising = useAppSelector(selectIsFinalisingSale)
  const saleManager = useSaleManager()
  const vdFeatureManager = useVdFeatureManager()
  const isValidateReturnEnabled = vdFeatureManager.get(
    FEATURE_VALIDATE_RETURN_QUANTITIES
  )
  const closeSaleConfirmation = useCloseSaleConfirmation(
    isPickedUp,
    setIsPickedUp,
    quotePaymentRequest,
    onShareLinkReady
  )
  const completeTutorial = useCompleteTutorial()
  const { isOffline } = useIsOffline()
  const isQuoteDeliveryEnabled = getFeatureFromCache(
    FEATURE_ENABLE_INVOICING_PAYMENT_REQUEST_QUOTE_DELIVERY
  )
  const isQuoteDeliveryPaymentRequestMissingAddress =
    isQuoteDeliveryEnabled &&
    quotePaymentRequest?.isToggleEnabled &&
    quotePaymentRequest.convertTo === 'delivery' &&
    !quotePaymentRequest.deliveryAddressDetails

  const disabled =
    isQuoteDeliveryPaymentRequestMissingAddress ||
    ((actionDisabled ||
      sale.syncStatus === SaleSyncStatus.UPLOAD_FIRST_SUBMIT ||
      isFinalising ||
      saleManager.shouldDisplayEmailErrorBanner()) &&
      // When offline, we don't want to check for these conditions,
      // Allow completing the sale.
      !isOffline)

  const completeSaleText = (() => {
    if (isQuote(sale.status)) {
      return <T _str="Complete quote" _context={COMPLETE_SALE_CONTEXT} />
    }
    if (isValidateReturnEnabled && saleIsExchange(sale)) {
      return <T _str="Complete exchange" _context={COMPLETE_SALE_CONTEXT} />
    }
    if (isValidateReturnEnabled && isReturn(sale)) {
      return <T _str="Complete return" _context={COMPLETE_SALE_CONTEXT} />
    }
    // Show "Done" only for ABP-only confirmation (no sale products present).
    if (isAbpOnlyConfirmationFlow(sale)) {
      return (
        <T
          _str="Done"
          _context="Finalize the account balance payment flow button"
        />
      )
    }
    return <T _str="Complete sale" _context={COMPLETE_SALE_CONTEXT} />
  })()

  return (
    <div className="sale-action-bar">
      {trainingModeManager.trainingModeOnboardingState ? (
        <Button
          onClick={completeTutorial}
          className="vd-btn vd-btn--go vd-btn--jumbo vd-flex--align-center vd-flex--justify-center btn--exit-mode sale-confirmation-form-row"
          data-track="complete-tutorial"
        >
          <T _str="Complete tutorial" _context={COMPLETE_SALE_CONTEXT} />
        </Button>
      ) : (
        <Button
          onClick={() => closeSaleConfirmation('button')}
          variant="go"
          jumbo
          disabled={disabled}
          data-cy="complete-action-button"
          data-testid="complete-action-button"
          className="btn-complete vd-flex--align-center vd-flex--justify-center sale-confirmation-form-row"
          data-track={
            trainingModeManager.trainingModeEnabled
              ? 'start-new-sale-in-training-mode'
              : 'complete-sale'
          }
        >
          {trainingModeManager.trainingModeEnabled ? (
            <T
              _str="Start a new sale in training mode"
              _context={COMPLETE_SALE_CONTEXT}
            />
          ) : (
            completeSaleText
          )}
        </Button>
      )}
    </div>
  )
}
